import { Fieldset, Label, Radios, TextInput } from "nhsuk-react-components"
import { useContext, useState } from "react"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import {
  getChoiceAnswerFromResponse,
  getChoiceOptions,
  getResponseForQuestion,
  type ChoiceAnswer,
} from "../../fhirUtils/questionnaireUtils"
import type { QuestionProps } from "./Question"
import { questionClass } from "./questionClass"
import { REQUIRED_ERROR_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireError } from "./QuestionnaireError"
import { showQuestion } from "./showQuestion"

/**
 * Single choice question using Radio Buttons.
 */
export function ChoiceQuestion({ question, saveResponses, topLevel }: QuestionProps): JSX.Element {
  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("ChoiceQuestion must be used within a QuestionnaireProvider")
  }
  const { responses, errors } = questionnaireContext

  const choiceOptions = getChoiceOptions(question)
  const response = getResponseForQuestion(question, responses)
  const [answer, setAnswer] = useState<ChoiceAnswer>(getChoiceAnswerFromResponse(choiceOptions, response))

  const { choices, other } = answer

  const updateAnswer = (updatedChoices: string[], otherValue: string): void => {
    const choicesToSave = otherValue.trim().length > 0 ? [...updatedChoices, otherValue.trim()] : updatedChoices
    setAnswer({ choices: updatedChoices, other: otherValue })
    saveResponses({
      linkId: question.linkId,
      text: question.text,
      answer: choicesToSave.map((valueString) => {
        return { valueString }
      }),
    })
  }

  const updateChoices = (updatedChoices: string[]): void => {
    updateAnswer(updatedChoices, "")
  }

  const updateOtherChoice = (otherChoice: string): void => {
    updateAnswer([], otherChoice)
  }

  const showRequiredError = !!errors[question.linkId]?.required

  const requiredErrorMessage = showRequiredError
    ? (question.extension?.find((ext) => ext.url === REQUIRED_ERROR_DEFINITION_URL)?.valueMarkdown ??
      "This field is required.")
    : null

  return (
    <form onSubmit={(e) => e.preventDefault()} aria-live="polite">
      {showRequiredError && !choices.length && (
        <QuestionnaireError formElementId={question.linkId} errorMessage={requiredErrorMessage} />
      )}
      {showQuestion(question, responses) && (
        <Fieldset className={questionClass(question, responses)} aria-live="assertive">
          {topLevel ? <Fieldset.Legend isPageHeading>{question.text}</Fieldset.Legend> : <Label>{question.text}</Label>}
          <Radios id={question.linkId} error={showRequiredError && !choices.length}>
            {question.option?.map((option) => {
              const optionValue = option.valueString ?? ""
              const isSelectedChoice = choices.includes(optionValue)

              const onClick = (): void => {
                if (isSelectedChoice) {
                  updateChoices([])
                } else {
                  updateChoices([optionValue])
                }
              }
              return (
                <Radios.Radio
                  value={optionValue}
                  checked={isSelectedChoice}
                  onClick={onClick}
                  key={optionValue}
                  aria-live="assertive"
                >
                  {optionValue}
                </Radios.Radio>
              )
            })}
            {question.type === "open-choice" && (
              <TextInput
                id="other"
                name="other"
                value={other}
                label="Other"
                aria-live="assertive"
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  updateOtherChoice(event.currentTarget.value)
                }}
              />
            )}
          </Radios>
        </Fieldset>
      )}
    </form>
  )
}
